import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    CopyTrackToClipboard,
    GetSpotifySearchUrl,
    GetYouTubeSearchUrl
} from '../../../../utilities/BL/Admin/Overlays/Playlist';
import { MESSAGE_TYPE } from '../../../Providers/FloatMessage';

/**
 * COMPONENTE: PlaylistActiveList (Playlist Activa / Cola de Cabina DJ)
 * 
 * Panel derecho de la Consola de Sonido para [HU-05] Arma la Playlist.
 * Permite gestionar la cola activa de reproducción, reordenar por Drag & Drop o botones táctiles,
 * controlar pista en vivo (Now Playing), marcar canciones como reproducidas y lanzar herramientas DJ
 * (Spotify, YouTube, Copiar).
 */
export const PlaylistActiveList = ({
    activePlaylist = [],
    nowPlayingSong = null,
    onSetNowPlaying = () => {},
    onTogglePlayed = () => {},
    onRemoveFromActive = () => {},
    onReorderPlaylist = () => {},
    showMessage = () => {},
    isLoading = false
}) => {
    // -------------------------------------------------------------------------
    // 1. ESTADOS LOCALES
    // -------------------------------------------------------------------------
    const [searchQuery, setSearchQuery] = useState('');
    const [draggedItemId, setDraggedItemId] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    // -------------------------------------------------------------------------
    // 2. UTILIDADES DE COPIA Y ENLACES EXTERNOS
    // -------------------------------------------------------------------------
    const handleCopy = async (title, artist) => {
        const result = await CopyTrackToClipboard(title, artist);
        if (result.success) {
            showMessage({
                type: MESSAGE_TYPE.SUCCESS,
                text: `📋 Copiado al portapapeles: "${result.text}"`
            });
        } else {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: result.message || 'No se pudo copiar la pista.'
            });
        }
    };

    const handleOpenSpotify = (title, artist) => {
        const url = GetSpotifySearchUrl(title, artist);
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handleOpenYouTube = (title, artist) => {
        const url = GetYouTubeSearchUrl(title, artist);
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    // -------------------------------------------------------------------------
    // 3. REORDENAMIENTO TÁCTIL (BOTONES SUBIR / BAJAR)
    // -------------------------------------------------------------------------
    const handleMoveItem = async (index, direction) => {
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= activePlaylist.length) return;

        const updatedList = [...activePlaylist];
        const [movedItem] = updatedList.splice(index, 1);
        updatedList.splice(targetIndex, 0, movedItem);

        const orderedIds = updatedList.map(item => item.id);
        await onReorderPlaylist(orderedIds);
    };

    // -------------------------------------------------------------------------
    // 4. REORDENAMIENTO NATIVO CON DRAG & DROP
    // -------------------------------------------------------------------------
    const handleDragStart = (e, item, index) => {
        setDraggedItemId(item.id);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', item.id);
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragOverIndex !== index) {
            setDragOverIndex(index);
        }
    };

    const handleDragLeave = (e) => {
        // Solo resetear si salimos del contenedor
    };

    const handleDrop = async (e, dropIndex) => {
        e.preventDefault();
        setDragOverIndex(null);

        const draggedId = e.dataTransfer.getData('text/plain') || draggedItemId;
        if (!draggedId) return;

        const currentIndex = activePlaylist.findIndex(item => item.id === draggedId);
        if (currentIndex === -1 || currentIndex === dropIndex) {
            setDraggedItemId(null);
            return;
        }

        const updatedList = [...activePlaylist];
        const [draggedItem] = updatedList.splice(currentIndex, 1);
        updatedList.splice(dropIndex, 0, draggedItem);

        setDraggedItemId(null);
        const orderedIds = updatedList.map(item => item.id);
        await onReorderPlaylist(orderedIds);
    };

    const handleDragEnd = () => {
        setDraggedItemId(null);
        setDragOverIndex(null);
    };

    // -------------------------------------------------------------------------
    // 5. CONTROL DJ: REPRODUCIR SIGUIENTE PISTA AUTOMÁTICAMENTE
    // -------------------------------------------------------------------------
    const handlePlayNextTrack = async () => {
        // Si hay una pista en vivo actual, la marcamos como reproducida
        if (nowPlayingSong) {
            await onTogglePlayed(nowPlayingSong.id, true);
        }

        // Buscar la primera pista en cola no reproducida y distinta a la actual
        const nextTrack = activePlaylist.find(track =>
            !track.isPlayed && (!nowPlayingSong || track.id !== nowPlayingSong.id)
        );

        if (nextTrack) {
            await onSetNowPlaying(nextTrack.id);
            showMessage({
                type: MESSAGE_TYPE.SUCCESS,
                text: `▶️ Sonando ahora: "${nextTrack.title}"`
            });
        } else {
            // Si no hay más pistas sin reproducir, avisar
            showMessage({
                type: MESSAGE_TYPE.INFO,
                text: 'Todas las pistas en la cola ya han sido reproducidas.'
            });
        }
    };

    // -------------------------------------------------------------------------
    // 6. FILTRO DE BÚSQUEDA INTERNA EN COLA ACTIVA
    // -------------------------------------------------------------------------
    const filteredActiveList = useMemo(() => {
        if (!Array.isArray(activePlaylist)) return [];
        if (!searchQuery.trim()) return activePlaylist;

        const query = searchQuery.toLowerCase().trim();
        return activePlaylist.filter(item => {
            const title = (item.title || '').toLowerCase();
            const artist = (item.artistName || '').toLowerCase();
            const fan = (item.fanName || '').toLowerCase();
            return title.includes(query) || artist.includes(query) || fan.includes(query);
        });
    }, [activePlaylist, searchQuery]);

    // Estadísticas de la cola
    const playedCount = activePlaylist.filter(item => item.isPlayed).length;
    const pendingCount = activePlaylist.length - playedCount;

    // -------------------------------------------------------------------------
    // 7. RENDERIZADO
    // -------------------------------------------------------------------------
    return (
        <div className="DJBoard__Panel DJBoard__Panel--ActiveQueue">
            {/* --- CABECERA DEL PANEL DERECHO --- */}
            <div className="DJBoard__PanelHeader">
                <div className="DJBoard__PanelHeader__TitleRow">
                    <div className="d-flex align-items-center gap-2">
                        <span className="DJBoard__PanelHeader__Icon">🎧</span>
                        <h2 className="DJBoard__PanelHeader__Title">Playlist Activa / Cola DJ</h2>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <span className="DJBoard__PanelHeader__CountBadge DJBoard__PanelHeader__CountBadge--Gold">
                            {activePlaylist.length} en cola
                        </span>
                    </div>
                </div>

                {/* Subcabecera con estadísticas y botón de Siguiente Pista */}
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-2">
                    <div className="DJBoard__QueueStats">
                        <span className="badge bg-dark border border-secondary text-white-50">
                            ⏳ {pendingCount} pendientes
                        </span>
                        <span className="badge bg-dark border border-secondary text-success">
                            ✅ {playedCount} reproducidas
                        </span>
                    </div>

                    <button
                        type="button"
                        className="DJBoard__PlayNextBtn"
                        onClick={handlePlayNextTrack}
                        disabled={activePlaylist.length === 0}
                        title="Pasa a la siguiente canción en cola automáticamente"
                    >
                        <i className="bi bi-skip-forward-fill me-1"></i> Siguiente Pista
                    </button>
                </div>

                {/* Buscador dentro de la cola activa */}
                <div className="DJBoard__SearchBox mt-2">
                    <i className="bi bi-search DJBoard__SearchBox__Icon"></i>
                    <input
                        type="text"
                        className="DJBoard__SearchBox__Input"
                        placeholder="Buscar dentro de la playlist activa..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                        <button
                            type="button"
                            className="DJBoard__SearchBox__ClearBtn"
                            onClick={() => setSearchQuery('')}
                            title="Limpiar búsqueda"
                        >
                            <i className="bi bi-x-circle-fill"></i>
                        </button>
                    )}
                </div>
            </div>

            {/* --- DECK / HERO BANNER DE NOW PLAYING EN VIVO --- */}
            {nowPlayingSong && (
                <div className="DJBoard__NowPlayingDeck">
                    <div className="DJBoard__NowPlayingDeck__Header">
                        <div className="d-flex align-items-center gap-2">
                            <span className="DJBoard__NowPlayingDeck__LivePill">
                                <span className="DJBoard__LiveDot"></span> EN VIVO EN PANTALLA
                            </span>
                            <div className="DJBoard__Equalizer">
                                <span className="DJBoard__Equalizer__Bar"></span>
                                <span className="DJBoard__Equalizer__Bar"></span>
                                <span className="DJBoard__Equalizer__Bar"></span>
                                <span className="DJBoard__Equalizer__Bar"></span>
                            </div>
                        </div>

                        {/* Botón para detener / desmontar Now Playing */}
                        <button
                            type="button"
                            className="DJBoard__NowPlayingDeck__StopBtn"
                            onClick={() => onSetNowPlaying(null)}
                            title="Desmarcar canción en vivo"
                        >
                            <i className="bi bi-stop-circle me-1"></i> Detener
                        </button>
                    </div>

                    <div className="DJBoard__NowPlayingDeck__Body">
                        <div className="DJBoard__NowPlayingDeck__Info">
                            <h3 className="DJBoard__NowPlayingDeck__Title">{nowPlayingSong.title}</h3>
                            <p className="DJBoard__NowPlayingDeck__Artist">
                                {nowPlayingSong.artistName || <span className="fst-italic text-muted">Artista no especificado</span>}
                            </p>
                            {nowPlayingSong.fanName && (
                                <span className="DJBoard__NowPlayingDeck__Fan">
                                    <i className="bi bi-person-heart me-1 text-danger"></i> Pedida por: {nowPlayingSong.fanName}
                                </span>
                            )}
                        </div>

                        {/* Acciones directas DJ para la pista en vivo */}
                        <div className="DJBoard__NowPlayingDeck__DjTools">
                            <button
                                type="button"
                                className="DJBoard__DjToolBtn DJBoard__DjToolBtn--Spotify"
                                onClick={() => handleOpenSpotify(nowPlayingSong.title, nowPlayingSong.artistName)}
                                title="Buscar en Spotify"
                            >
                                <i className="bi bi-spotify"></i> Spotify
                            </button>
                            <button
                                type="button"
                                className="DJBoard__DjToolBtn DJBoard__DjToolBtn--YouTube"
                                onClick={() => handleOpenYouTube(nowPlayingSong.title, nowPlayingSong.artistName)}
                                title="Buscar en YouTube"
                            >
                                <i className="bi bi-youtube"></i> YouTube
                            </button>
                            <button
                                type="button"
                                className="DJBoard__DjToolBtn DJBoard__DjToolBtn--Copy"
                                onClick={() => handleCopy(nowPlayingSong.title, nowPlayingSong.artistName)}
                                title="Copiar nombre al portapapeles"
                            >
                                <i className="bi bi-clipboard"></i> Copiar
                            </button>
                            <button
                                type="button"
                                className={`DJBoard__DjToolBtn DJBoard__DjToolBtn--Played ${nowPlayingSong.isPlayed ? 'is-played' : ''}`}
                                onClick={() => onTogglePlayed(nowPlayingSong.id, !nowPlayingSong.isPlayed)}
                                title="Alternar estado de reproducida"
                            >
                                <i className={`bi ${nowPlayingSong.isPlayed ? 'bi-check2-circle text-success' : 'bi-check2'}`}></i>
                                {nowPlayingSong.isPlayed ? 'Sonada' : 'Marcar Sonada'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- LISTADO REORDENABLE DE LA COLA --- */}
            <div className="DJBoard__PanelBody">
                {isLoading && activePlaylist.length === 0 ? (
                    <div className="DJBoard__EmptyState">
                        <div className="spinner-border text-danger mb-3" role="status"></div>
                        <p className="DJBoard__EmptyState__Text">Cargando cola de reproducción...</p>
                    </div>
                ) : filteredActiveList.length === 0 ? (
                    <div className="DJBoard__EmptyState">
                        <i className="bi bi-disc DJBoard__EmptyState__Icon"></i>
                        <p className="DJBoard__EmptyState__Text">
                            {searchQuery
                                ? 'No se encontraron canciones activas que coincidan.'
                                : 'La playlist activa está vacía.'}
                        </p>
                        <p className="small text-muted">
                            {searchQuery
                                ? 'Prueba con otro término de búsqueda.'
                                : '⭐ Marca sugerencias con la estrella en la bandeja izquierda para armar la cola del DJ.'}
                        </p>
                    </div>
                ) : (
                    <div className="DJBoard__QueueList">
                        {filteredActiveList.map((item, index) => {
                            const isLive = (nowPlayingSong && nowPlayingSong.id === item.id) || item.isNowPlaying;
                            const isBeingDragged = draggedItemId === item.id;
                            const isDropTarget = dragOverIndex === index;

                            return (
                                <div
                                    key={item.id}
                                    className={`DJBoard__QueueCard ${isLive ? 'is-now-playing' : ''} ${item.isPlayed ? 'is-played' : ''} ${isBeingDragged ? 'is-dragging' : ''} ${isDropTarget ? 'is-dragover' : ''}`}
                                    draggable={!searchQuery} // Deshabilitar drag si hay filtro de texto activo
                                    onDragStart={(e) => handleDragStart(e, item, index)}
                                    onDragOver={(e) => handleDragOver(e, index)}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) => handleDrop(e, index)}
                                    onDragEnd={handleDragEnd}
                                >
                                    {/* Indicador de posición / Drag Handle */}
                                    <div className="DJBoard__QueueCard__HandleCol" title="Arrastra para reordenar la cola">
                                        <i className="bi bi-grip-vertical DJBoard__QueueCard__GripIcon"></i>
                                        <span className="DJBoard__QueueCard__PositionNumber">
                                            #{index + 1}
                                        </span>
                                    </div>

                                    {/* Información central de la pista */}
                                    <div className="DJBoard__QueueCard__TrackCol">
                                        <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                                            {isLive && (
                                                <span className="DJBoard__LiveBadge">
                                                    <span className="DJBoard__LiveDot"></span> EN VIVO
                                                </span>
                                            )}

                                            {item.isPlayed && (
                                                <span className="DJBoard__PlayedBadge">
                                                    <i className="bi bi-check2-all me-1"></i> Reproducida
                                                </span>
                                            )}

                                            {item.voteCount > 1 && (
                                                <span className="DJBoard__PopularityBadge">
                                                    🔥 {item.voteCount} votos
                                                </span>
                                            )}
                                        </div>

                                        <h3 className={`DJBoard__QueueCard__Title ${item.isPlayed ? 'is-strike' : ''}`}>
                                            {item.title}
                                        </h3>
                                        <p className="DJBoard__QueueCard__Artist">
                                            {item.artistName ? item.artistName : <span className="text-muted fst-italic">Artista no especificado</span>}
                                        </p>

                                        {item.fanName && (
                                            <span className="DJBoard__QueueCard__Fan">
                                                <i className="bi bi-person text-muted me-1"></i> {item.fanName}
                                            </span>
                                        )}
                                    </div>

                                    {/* Herramientas DJ (Spotify, YouTube, Copiar) */}
                                    <div className="DJBoard__QueueCard__DjToolsCol">
                                        <button
                                            type="button"
                                            className="DJBoard__DjToolIconBtn DJBoard__DjToolIconBtn--Copy"
                                            onClick={() => handleCopy(item.title, item.artistName)}
                                            title="Copiar pista al portapapeles"
                                        >
                                            <i className="bi bi-clipboard"></i>
                                        </button>
                                        <button
                                            type="button"
                                            className="DJBoard__DjToolIconBtn DJBoard__DjToolIconBtn--Spotify"
                                            onClick={() => handleOpenSpotify(item.title, item.artistName)}
                                            title="Abrir búsqueda en Spotify"
                                        >
                                            <i className="bi bi-spotify"></i>
                                        </button>
                                        <button
                                            type="button"
                                            className="DJBoard__DjToolIconBtn DJBoard__DjToolIconBtn--YouTube"
                                            onClick={() => handleOpenYouTube(item.title, item.artistName)}
                                            title="Abrir búsqueda en YouTube"
                                        >
                                            <i className="bi bi-youtube"></i>
                                        </button>
                                    </div>

                                    {/* Controles de Reproducción y Posición Táctil */}
                                    <div className="DJBoard__QueueCard__ControlsCol">
                                        {/* Now Playing toggle */}
                                        <button
                                            type="button"
                                            className={`DJBoard__PlayBtn ${isLive ? 'is-active' : ''}`}
                                            onClick={() => onSetNowPlaying(isLive ? null : item.id)}
                                            title={isLive ? 'Desmarcar Now Playing' : 'Poner EN VIVO como Now Playing'}
                                        >
                                            <i className={`bi ${isLive ? 'bi-stop-fill' : 'bi-play-fill'}`}></i>
                                        </button>

                                        {/* Toggle Reproducida */}
                                        <button
                                            type="button"
                                            className={`DJBoard__PlayedBtn ${item.isPlayed ? 'is-active' : ''}`}
                                            onClick={() => onTogglePlayed(item.id, !item.isPlayed)}
                                            title={item.isPlayed ? 'Marcar como pendiente' : 'Marcar como reproducida'}
                                        >
                                            <i className="bi bi-check2"></i>
                                        </button>

                                        {/* Botones de Orden Táctil (Subir / Bajar) */}
                                        <div className="DJBoard__TouchReorderGroup">
                                            <button
                                                type="button"
                                                className="DJBoard__TouchReorderBtn"
                                                onClick={() => handleMoveItem(index, -1)}
                                                disabled={index === 0}
                                                title="Mover arriba"
                                            >
                                                <i className="bi bi-chevron-up"></i>
                                            </button>
                                            <button
                                                type="button"
                                                className="DJBoard__TouchReorderBtn"
                                                onClick={() => handleMoveItem(index, 1)}
                                                disabled={index === activePlaylist.length - 1}
                                                title="Mover abajo"
                                            >
                                                <i className="bi bi-chevron-down"></i>
                                            </button>
                                        </div>

                                        {/* Quitar de la Playlist Activa */}
                                        <button
                                            type="button"
                                            className="DJBoard__RemoveBtn"
                                            onClick={() => onRemoveFromActive(item.id, item.title)}
                                            title="Quitar de la Playlist Activa"
                                        >
                                            <i className="bi bi-x-lg"></i>
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

PlaylistActiveList.propTypes = {
    activePlaylist: PropTypes.array,
    nowPlayingSong: PropTypes.object,
    onSetNowPlaying: PropTypes.func,
    onTogglePlayed: PropTypes.func,
    onRemoveFromActive: PropTypes.func,
    onReorderPlaylist: PropTypes.func,
    showMessage: PropTypes.func,
    isLoading: PropTypes.bool
};

export default PlaylistActiveList;
